import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PageHeader from './common/PageHeader';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloseIcon from '@mui/icons-material/Close';
import CampaignIcon from '@mui/icons-material/Campaign';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import campaignApi from '../api/campaignApi';

const initialForm = {
  name: '',
  channel: 'SMS',
  message_type: 'text',
  content_template: '',
  whatsapp_template_name: '',
  whatsapp_language_code: 'ar',
  schedule_enabled: false,
  schedule_at: '',
  schedule_timezone: 'Asia/Riyadh',
  numbers_text: '',
  csv_file: null,
};

const LOCAL_DB_STORAGE_KEY = 'campaign_numbers_database_v1';

const statusColorMap = {
  DRAFT: 'default',
  SCHEDULED: 'warning',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  PARTIAL: 'warning',
  QUEUED: 'default',
  SUBMITTED: 'info',
  DELIVERED: 'success',
  UNDELIVERABLE: 'warning',
  EXPIRED: 'warning',
};

const normalizePhone = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  let cleaned = String(value).trim();
  if (!cleaned) {
    return '';
  }

  cleaned = cleaned.replace(/[^\d+]/g, '');

  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`;
  }

  const hasPlus = cleaned.startsWith('+');
  const digits = cleaned.replace(/\D/g, '');

  if (digits.length < 8) {
    return '';
  }

  return hasPlus ? `+${digits}` : digits;
};

const parseNumbers = (value) =>
  Array.from(new Set((value || '')
    .split(/[\n,]+/)
    .map((item) => normalizePhone(item))
    .filter(Boolean)));

const generateDbEntry = (phone) => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  phone,
});

const parseSpreadsheetPhones = async (file) => {
  const fileBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(fileBuffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    return [];
  }

  const sheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  const extracted = rows
    .map((row) => {
      if (!Array.isArray(row) || row.length === 0) {
        return '';
      }

      for (const cell of row) {
        const candidates = parseNumbers(String(cell || ''));
        if (candidates.length > 0) {
          return candidates[0];
        }
      }

      return '';
    })
    .filter(Boolean);

  return Array.from(new Set(extracted));
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString('ar-SA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function CampaignsPage() {
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [form, setForm] = useState(initialForm);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsPage, setRecipientsPage] = useState(0);
  const [recipientsPageSize, setRecipientsPageSize] = useState(100);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [filters, setFilters] = useState({ channel: '', status: '' });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phoneDatabase, setPhoneDatabase] = useState([]);
  const [databaseError, setDatabaseError] = useState('');
  const [databaseSuccess, setDatabaseSuccess] = useState('');
  const [databaseUploading, setDatabaseUploading] = useState(false);
  const [selectedDatabaseIds, setSelectedDatabaseIds] = useState(new Set());
  const [useSavedDatabase, setUseSavedDatabase] = useState(true);
  const [newDbNumber, setNewDbNumber] = useState('');
  const [editingDbId, setEditingDbId] = useState('');
  const [editingDbNumber, setEditingDbNumber] = useState('');
  const [rangeStartPhone, setRangeStartPhone] = useState('');
  const [rangeEndPhone, setRangeEndPhone] = useState('');
  const [dbPage, setDbPage] = useState(0);
  const [dbRowsPerPage, setDbRowsPerPage] = useState(25);
  const [reuseLoading, setReuseLoading] = useState(false);

  const isWhatsappTemplate = form.channel === 'WHATSAPP' && form.message_type === 'template';

  useEffect(() => {
    const savedRaw = localStorage.getItem(LOCAL_DB_STORAGE_KEY);
    if (!savedRaw) {
      return;
    }

    try {
      const parsed = JSON.parse(savedRaw);
      if (!Array.isArray(parsed)) {
        return;
      }

      const normalizedEntries = parsed
        .map((entry) => {
          if (typeof entry === 'string') {
            const phone = normalizePhone(entry);
            return phone ? generateDbEntry(phone) : null;
          }

          if (!entry || typeof entry !== 'object') {
            return null;
          }

          const phone = normalizePhone(entry.phone);
          if (!phone) {
            return null;
          }

          return {
            id: entry.id || generateDbEntry(phone).id,
            phone,
          };
        })
        .filter(Boolean);

      setPhoneDatabase(normalizedEntries);
      setSelectedDatabaseIds(new Set(normalizedEntries.map((item) => item.id)));
      setUseSavedDatabase(normalizedEntries.length > 0);
    } catch (loadError) {
      console.error('Failed to load saved phone database', loadError);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCAL_DB_STORAGE_KEY, JSON.stringify(phoneDatabase));
  }, [phoneDatabase]);

  const selectedDatabaseNumbers = useMemo(
    () => phoneDatabase.filter((item) => selectedDatabaseIds.has(item.id)).map((item) => item.phone),
    [phoneDatabase, selectedDatabaseIds],
  );

  const paginatedDatabaseRows = useMemo(() => {
    const start = dbPage * dbRowsPerPage;
    return phoneDatabase.slice(start, start + dbRowsPerPage);
  }, [phoneDatabase, dbPage, dbRowsPerPage]);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await campaignApi.list(filters);
      setCampaigns(data);
      setSelectedCampaign((current) => {
        if (!data.length) {
          return null;
        }
        if (current) {
          return data.find((item) => item.id === current.id) || data[0];
        }
        return data[0];
      });
    } catch (err) {
      setError(err.message || 'فشل في تحميل الحملات.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadRecipients = useCallback(async (campaignId, page = 0, pageSize = recipientsPageSize) => {
    if (!campaignId) {
      setRecipients([]);
      setRecipientsTotal(0);
      return;
    }

    setRecipientsLoading(true);
    try {
      const data = await campaignApi.getRecipients(campaignId, {
        page: page + 1,
        page_size: pageSize,
      });
      const recipientRows = Array.isArray(data) ? data : (data.results || []);
      setRecipients(recipientRows);
      setRecipientsTotal(Array.isArray(data) ? data.length : (data.count || 0));
      setRecipientsPage(page);
      setRecipientsPageSize(pageSize);
    } catch (err) {
      setError(err.message || 'فشل في تحميل حالات المستلمين.');
      setRecipients([]);
      setRecipientsTotal(0);
    } finally {
      setRecipientsLoading(false);
    }
  }, [recipientsPageSize]);

  useEffect(() => {
    if (isAdmin) {
      setError('صفحة الحملات متاحة لحسابات العملاء فقط وليست لحساب الأدمن.');
      setLoading(false);
      return;
    }
    loadCampaigns();
  }, [isAdmin, loadCampaigns]);

  useEffect(() => {
    if (selectedCampaign?.id) {
      loadRecipients(selectedCampaign.id, 0, recipientsPageSize);
    } else {
      setRecipients([]);
      setRecipientsTotal(0);
    }
  }, [selectedCampaign, loadRecipients, recipientsPageSize]);

  const statsSummary = useMemo(() => {
    if (!selectedCampaign) {
      return [];
    }

    return [
      { label: 'الإجمالي', value: selectedCampaign.total_recipients },
      { label: 'تم الإرسال', value: selectedCampaign.sent_count },
      { label: 'تم التسليم', value: selectedCampaign.delivered_count },
      { label: 'فشل', value: selectedCampaign.failed_count },
    ];
  }, [selectedCampaign]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreateCampaign = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');

    const hasCsv = Boolean(form.csv_file);
    const numbersFromText = parseNumbers(form.numbers_text);
    const numbersFromDatabase = useSavedDatabase ? selectedDatabaseNumbers : [];
    const numbers = Array.from(new Set([...numbersFromText, ...numbersFromDatabase]));

    if (form.message_type === 'text' && !form.content_template.trim()) {
      setError('نص الرسالة مطلوب.');
      setSubmitting(false);
      return;
    }

    if (isWhatsappTemplate && !form.whatsapp_template_name.trim()) {
      setError('اسم قالب واتساب مطلوب.');
      setSubmitting(false);
      return;
    }

    if (!hasCsv && numbers.length === 0) {
      setError('أدخل أرقامًا أو ارفع CSV أو استخدم قاعدة الأرقام المحفوظة.');
      setSubmitting(false);
      return;
    }

    try {
      const scheduleAt = form.schedule_enabled && form.schedule_at ? form.schedule_at : null;
      let createdCampaign;

      if (hasCsv) {
        const payload = new FormData();
        payload.append('name', form.name);
        payload.append('channel', form.channel);
        payload.append('message_type', form.channel === 'SMS' ? 'text' : form.message_type);
        payload.append('content_template', form.content_template);
        payload.append('whatsapp_template_name', form.whatsapp_template_name);
        payload.append('whatsapp_language_code', form.whatsapp_language_code);
        payload.append('schedule_timezone', form.schedule_timezone);
        payload.append('csv_file', form.csv_file);
        if (numbers.length > 0) {
          payload.append('numbers', numbers.join('\n'));
        }
        if (scheduleAt) {
          payload.append('schedule_at', scheduleAt);
        }
        createdCampaign = await campaignApi.create(payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        createdCampaign = await campaignApi.create({
          name: form.name,
          channel: form.channel,
          message_type: form.channel === 'SMS' ? 'text' : form.message_type,
          content_template: form.content_template,
          whatsapp_template_name: form.whatsapp_template_name,
          whatsapp_language_code: form.whatsapp_language_code,
          schedule_at: scheduleAt,
          schedule_timezone: form.schedule_timezone,
          numbers,
        });
      }

      setForm(initialForm);
      setSuccess(`تم إنشاء الحملة #${createdCampaign.id} بنجاح (${numbers.length} رقم من المدخلات المختارة).`);
      await loadCampaigns();
      setSelectedCampaign(createdCampaign);
      await loadRecipients(createdCampaign.id, 0, recipientsPageSize);
    } catch (err) {
      setError(err.message || 'فشل في إنشاء الحملة.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunNow = async (campaignId) => {
    setError('');
    setSuccess('');
    try {
      const updatedCampaign = await campaignApi.runNow(campaignId);
      setSuccess(`تم تشغيل الحملة #${campaignId} الآن.`);
      await loadCampaigns();
      setSelectedCampaign(updatedCampaign);
      await loadRecipients(updatedCampaign.id, 0, recipientsPageSize);
    } catch (err) {
      setError(err.message || 'فشل في تشغيل الحملة.');
    }
  };

  const handleRetryFailed = async (campaignId) => {
    setError('');
    setSuccess('');
    try {
      const updatedCampaign = await campaignApi.retryFailed(campaignId);
      setSuccess(`Retried failed recipients for campaign #${campaignId}.`);
      await loadCampaigns();
      setSelectedCampaign(updatedCampaign);
      await loadRecipients(updatedCampaign.id, recipientsPage, recipientsPageSize);
    } catch (err) {
      setError(err.message || 'Failed to retry failed recipients.');
    }
  };

  const handleUploadDatabaseFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setDatabaseError('');
    setDatabaseSuccess('');
    setDatabaseUploading(true);
    try {
      const phones = await parseSpreadsheetPhones(file);
      if (phones.length === 0) {
        setDatabaseError('لم يتم العثور على أرقام صالحة داخل الملف.');
        return;
      }

      const databaseRows = phones.map((phone) => generateDbEntry(phone));
      setPhoneDatabase(databaseRows);
      setSelectedDatabaseIds(new Set(databaseRows.map((item) => item.id)));
      setUseSavedDatabase(true);
      setDbPage(0);
      setDatabaseSuccess(`تم رفع قاعدة الأرقام بنجاح: ${phones.length} رقم.`);
    } catch (uploadError) {
      setDatabaseError('فشل في قراءة الملف. تأكد أنه Excel/CSV صالح.');
    } finally {
      setDatabaseUploading(false);
    }
  };

  const handleAddNumberToDatabase = () => {
    const normalized = normalizePhone(newDbNumber);
    if (!normalized) {
      setDatabaseError('الرقم غير صالح.');
      return;
    }

    if (phoneDatabase.some((item) => item.phone === normalized)) {
      setDatabaseError('هذا الرقم موجود بالفعل في قاعدة البيانات.');
      return;
    }

    const nextEntry = generateDbEntry(normalized);
    setPhoneDatabase((current) => [nextEntry, ...current]);
    setSelectedDatabaseIds((current) => new Set([...current, nextEntry.id]));
    setNewDbNumber('');
    setDatabaseError('');
    setDatabaseSuccess('تمت إضافة الرقم بنجاح.');
  };

  const handleDeleteDatabaseNumber = (id) => {
    setPhoneDatabase((current) => current.filter((item) => item.id !== id));
    setSelectedDatabaseIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  };

  const handleDeleteSelectedDatabaseNumbers = () => {
    if (selectedDatabaseIds.size === 0) {
      setDatabaseError('اختر أرقامًا أولاً للحذف.');
      return;
    }

    setPhoneDatabase((current) => current.filter((item) => !selectedDatabaseIds.has(item.id)));
    setSelectedDatabaseIds(new Set());
    setDatabaseSuccess('تم حذف الأرقام المحددة.');
  };

  const toggleDatabaseSelection = (id) => {
    setSelectedDatabaseIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAllDatabaseNumbers = () => {
    setSelectedDatabaseIds(new Set(phoneDatabase.map((item) => item.id)));
  };

  const handleClearDatabaseSelection = () => {
    setSelectedDatabaseIds(new Set());
  };

  const handleSelectDatabaseRange = () => {
    const start = normalizePhone(rangeStartPhone);
    const end = normalizePhone(rangeEndPhone);

    if (!start || !end) {
      setDatabaseError('أدخل رقم البداية ورقم النهاية بشكل صحيح.');
      return;
    }

    const startIndex = phoneDatabase.findIndex((item) => item.phone === start);
    const endIndex = phoneDatabase.findIndex((item) => item.phone === end);

    if (startIndex < 0 || endIndex < 0) {
      setDatabaseError('لم يتم العثور على رقم البداية أو النهاية في قاعدة البيانات.');
      return;
    }

    const from = Math.min(startIndex, endIndex);
    const to = Math.max(startIndex, endIndex);
    const rangeIds = phoneDatabase.slice(from, to + 1).map((item) => item.id);

    setSelectedDatabaseIds(new Set(rangeIds));
    setDatabaseError('');
    setDatabaseSuccess(`تم تحديد مدى يحتوي على ${rangeIds.length} رقم.`);
  };

  const handleStartEditDatabaseNumber = (row) => {
    setEditingDbId(row.id);
    setEditingDbNumber(row.phone);
  };

  const handleSaveEditDatabaseNumber = () => {
    const normalized = normalizePhone(editingDbNumber);
    if (!normalized) {
      setDatabaseError('الرقم بعد التعديل غير صالح.');
      return;
    }

    if (phoneDatabase.some((item) => item.phone === normalized && item.id !== editingDbId)) {
      setDatabaseError('هذا الرقم موجود مسبقًا.');
      return;
    }

    setPhoneDatabase((current) =>
      current.map((item) => (item.id === editingDbId ? { ...item, phone: normalized } : item)),
    );
    setEditingDbId('');
    setEditingDbNumber('');
    setDatabaseError('');
    setDatabaseSuccess('تم تعديل الرقم بنجاح.');
  };

  const loadAllCampaignRecipientNumbers = useCallback(async (campaignId) => {
    const allNumbers = [];
    let nextPage = 1;
    const pageSize = 250;
    let hasMore = true;

    while (hasMore) {
      const data = await campaignApi.getRecipients(campaignId, {
        page: nextPage,
        page_size: pageSize,
      });

      if (Array.isArray(data)) {
        const fromArray = data.map((item) => normalizePhone(item.phone_e164 || item.phone || '')).filter(Boolean);
        allNumbers.push(...fromArray);
        hasMore = false;
        continue;
      }

      const rows = (data.results || [])
        .map((item) => normalizePhone(item.phone_e164 || item.phone || ''))
        .filter(Boolean);
      allNumbers.push(...rows);

      if (data.next) {
        nextPage += 1;
      } else {
        hasMore = false;
      }
    }

    return Array.from(new Set(allNumbers));
  }, []);

  const applyCampaignToForm = (campaign, numbers = []) => {
    const nextChannel = campaign.channel || 'SMS';
    const nextMessageType = nextChannel === 'SMS' ? 'text' : (campaign.message_type || 'text');
    const nextName = campaign.name
      ? `نسخة من ${campaign.name}`
      : `نسخة من حملة #${campaign.id}`;

    setForm((current) => ({
      ...current,
      name: nextName,
      channel: nextChannel,
      message_type: nextMessageType,
      content_template: campaign.content_template || '',
      whatsapp_template_name: campaign.whatsapp_template_name || '',
      whatsapp_language_code: campaign.whatsapp_language_code || 'ar',
      schedule_enabled: false,
      schedule_at: '',
      schedule_timezone: campaign.schedule_timezone || 'Asia/Riyadh',
      numbers_text: numbers.join('\n'),
      csv_file: null,
    }));
  };

  const handleReuseCampaign = async (withNumbers) => {
    if (!selectedCampaign?.id) {
      return;
    }

    setError('');
    setSuccess('');
    setReuseLoading(true);
    try {
      let numbers = [];
      if (withNumbers) {
        numbers = await loadAllCampaignRecipientNumbers(selectedCampaign.id);
      }

      applyCampaignToForm(selectedCampaign, numbers);
      setSuccess(
        withNumbers
          ? `تم تجهيز نسخة من الحملة #${selectedCampaign.id} مع ${numbers.length} رقم. يمكنك تعديل الرسالة أو الأرقام قبل الإنشاء.`
          : `تم تجهيز نسخة من الحملة #${selectedCampaign.id}. يمكنك إضافة/تعديل الأرقام ثم الإنشاء.`,
      );
    } catch (err) {
      setError(err.message || 'فشل في تجهيز نسخة الحملة.');
    } finally {
      setReuseLoading(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="حملات الرسائل"
        subtitle="إنشاء حملات SMS وWhatsApp مع جدولة ومتابعة حالة كل رقم"
        onRefresh={loadCampaigns}
      />
      <Stack spacing={3}>

        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={5}>
            <Paper sx={{ p: 3, borderRadius: 4, height: '100%' }}>
              <Stack spacing={2.5}>
                <Typography variant="h6" fontWeight={700}>
                  إنشاء حملة
                </Typography>

                <TextField
                  label="اسم الحملة"
                  value={form.name}
                  onChange={(event) => updateForm('name', event.target.value)}
                  fullWidth
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>القناة</InputLabel>
                      <Select
                        value={form.channel}
                        label="القناة"
                        onChange={(event) => updateForm('channel', event.target.value)}
                      >
                        <MenuItem value="SMS">SMS</MenuItem>
                        <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={form.channel === 'SMS'}>
                      <InputLabel>نوع الرسالة</InputLabel>
                      <Select
                        value={form.channel === 'SMS' ? 'text' : form.message_type}
                        label="نوع الرسالة"
                        onChange={(event) => updateForm('message_type', event.target.value)}
                      >
                        <MenuItem value="text">نص</MenuItem>
                        <MenuItem value="template">قالب واتساب</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {isWhatsappTemplate && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <TextField
                        label="اسم قالب واتساب"
                        value={form.whatsapp_template_name}
                        onChange={(event) => updateForm('whatsapp_template_name', event.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="لغة القالب"
                        value={form.whatsapp_language_code}
                        onChange={(event) => updateForm('whatsapp_language_code', event.target.value)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                )}

                <TextField
                  label="نص الرسالة"
                  value={form.content_template}
                  onChange={(event) => updateForm('content_template', event.target.value)}
                  multiline
                  minRows={5}
                  fullWidth
                  helperText="يمكن استخدام متغيرات مثل {name} مع CSV."
                />

                <TextField
                  label="الأرقام"
                  value={form.numbers_text}
                  onChange={(event) => updateForm('numbers_text', event.target.value)}
                  multiline
                  minRows={4}
                  fullWidth
                  helperText="كل رقم في سطر أو مفصول بفاصلة."
                />

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
                    رفع CSV
                    <input
                      hidden
                      type="file"
                      accept=".csv,text/csv"
                      onChange={(event) => updateForm('csv_file', event.target.files?.[0] || null)}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {form.csv_file ? form.csv_file.name : 'اختياري: phone + المتغيرات.'}
                  </Typography>
                </Stack>

                <Divider />

                <Stack spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    قاعدة أرقام العميل
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      disabled={databaseUploading}
                    >
                      رفع Excel / CSV
                      <input
                        hidden
                        type="file"
                        accept=".xlsx,.xls,.csv,text/csv"
                        onChange={handleUploadDatabaseFile}
                      />
                    </Button>
                    <FormControlLabel
                      control={<Switch checked={useSavedDatabase} onChange={(event) => setUseSavedDatabase(event.target.checked)} />}
                      label="استخدم المحدد من القاعدة في الحملة"
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={`إجمالي القاعدة: ${phoneDatabase.length}`} size="small" />
                    <Chip label={`المحدد للإرسال: ${selectedDatabaseNumbers.length}`} size="small" color="primary" />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      label="من رقم"
                      value={rangeStartPhone}
                      onChange={(event) => setRangeStartPhone(event.target.value)}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      label="إلى رقم"
                      value={rangeEndPhone}
                      onChange={(event) => setRangeEndPhone(event.target.value)}
                      fullWidth
                    />
                    <Button variant="outlined" onClick={handleSelectDatabaseRange}>
                      تحديد مدى
                    </Button>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      size="small"
                      label="إضافة رقم جديد"
                      value={newDbNumber}
                      onChange={(event) => setNewDbNumber(event.target.value)}
                      fullWidth
                    />
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddNumberToDatabase}>
                      إضافة
                    </Button>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<CheckCircleOutlineIcon />}
                      onClick={handleSelectAllDatabaseNumbers}
                    >
                      تحديد الكل
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<ClearAllIcon />}
                      onClick={handleClearDatabaseSelection}
                    >
                      إلغاء التحديد
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="text"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteSelectedDatabaseNumbers}
                    >
                      حذف المحدد
                    </Button>
                  </Stack>

                  {databaseError && <Alert severity="error">{databaseError}</Alert>}
                  {databaseSuccess && <Alert severity="success">{databaseSuccess}</Alert>}

                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">اختيار</TableCell>
                          <TableCell>الرقم</TableCell>
                          <TableCell align="right">إجراءات</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDatabaseRows.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedDatabaseIds.has(row.id)}
                                onChange={() => toggleDatabaseSelection(row.id)}
                              />
                            </TableCell>
                            <TableCell>
                              {editingDbId === row.id ? (
                                <TextField
                                  size="small"
                                  value={editingDbNumber}
                                  onChange={(event) => setEditingDbNumber(event.target.value)}
                                  fullWidth
                                />
                              ) : (
                                row.phone
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {editingDbId === row.id ? (
                                <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                  <Tooltip title="حفظ">
                                    <IconButton size="small" color="primary" onClick={handleSaveEditDatabaseNumber}>
                                      <SaveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="إلغاء">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setEditingDbId('');
                                        setEditingDbNumber('');
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                  <Tooltip title="تعديل">
                                    <IconButton size="small" onClick={() => handleStartEditDatabaseNumber(row)}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="حذف">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteDatabaseNumber(row.id)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {!databaseUploading && phoneDatabase.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>لا توجد أرقام محفوظة بعد. ارفع ملف Excel/CSV.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={phoneDatabase.length}
                    page={dbPage}
                    onPageChange={(_event, nextPage) => setDbPage(nextPage)}
                    rowsPerPage={dbRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      const nextSize = Number(event.target.value) || 25;
                      setDbRowsPerPage(nextSize);
                      setDbPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="صفوف القاعدة"
                  />
                </Stack>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.schedule_enabled}
                      onChange={(event) => updateForm('schedule_enabled', event.target.checked)}
                    />
                  }
                  label="جدولة الحملة"
                />

                {form.schedule_enabled && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                      <TextField
                        label="موعد التشغيل"
                        type="datetime-local"
                        value={form.schedule_at}
                        onChange={(event) => updateForm('schedule_at', event.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        label="المنطقة الزمنية"
                        value={form.schedule_timezone}
                        onChange={(event) => updateForm('schedule_timezone', event.target.value)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                )}

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SendIcon />}
                  disabled={submitting}
                  onClick={handleCreateCampaign}
                >
                  {submitting ? 'جارٍ الإنشاء...' : 'إنشاء الحملة'}
                </Button>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Stack spacing={3}>
              <Paper sx={{ p: 3, borderRadius: 4 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    قائمة الحملات
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>القناة</InputLabel>
                      <Select
                        value={filters.channel}
                        label="القناة"
                        onChange={(event) => setFilters((current) => ({ ...current, channel: event.target.value }))}
                      >
                        <MenuItem value="">الكل</MenuItem>
                        <MenuItem value="SMS">SMS</MenuItem>
                        <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>الحالة</InputLabel>
                      <Select
                        value={filters.status}
                        label="الحالة"
                        onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                      >
                        <MenuItem value="">الكل</MenuItem>
                        {['SCHEDULED', 'PROCESSING', 'COMPLETED', 'FAILED', 'PARTIAL'].map((statusValue) => (
                          <MenuItem key={statusValue} value={statusValue}>
                            {statusValue}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>

                <Stack spacing={1.5}>
                  {campaigns.map((campaign) => (
                    <Paper
                      key={campaign.id}
                      variant="outlined"
                      onClick={() => setSelectedCampaign(campaign)}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        cursor: 'pointer',
                        borderColor: selectedCampaign?.id === campaign.id ? 'primary.main' : 'divider',
                        backgroundColor: selectedCampaign?.id === campaign.id ? 'rgba(14, 165, 233, 0.06)' : 'background.paper',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <CampaignIcon color="primary" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={700}>
                              {campaign.name || `حملة #${campaign.id}`}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {campaign.channel} • {campaign.message_type} • {formatDateTime(campaign.created_at)}
                          </Typography>
                        </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                          <Chip label={campaign.status} size="small" color={statusColorMap[campaign.status] || 'default'} />
                          {campaign.status === 'SCHEDULED' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PlayArrowIcon />}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRunNow(campaign.id);
                              }}
                            >
                              تشغيل الآن
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}

                  {!loading && campaigns.length === 0 && (
                    <Alert severity="info">لا توجد حملات بعد.</Alert>
                  )}
                </Stack>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  تفاصيل الحملة
                </Typography>

                {!selectedCampaign && <Alert severity="info">اختر حملة لعرض التفاصيل.</Alert>}

                {selectedCampaign && (
                  <Stack spacing={2}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip label={`الحالة: ${selectedCampaign.status}`} color={statusColorMap[selectedCampaign.status] || 'default'} />
                      <Chip label={`القناة: ${selectedCampaign.channel}`} variant="outlined" />
                      <Chip label={`الجدولة: ${formatDateTime(selectedCampaign.schedule_at_utc)}`} variant="outlined" />
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => handleReuseCampaign(false)}
                        disabled={reuseLoading}
                      >
                        إعادة استخدام المحتوى
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => handleReuseCampaign(true)}
                        disabled={reuseLoading}
                      >
                        {reuseLoading ? 'جارٍ تجهيز النسخة...' : 'إعادة استخدام مع الأرقام'}
                      </Button>
                    </Stack>

                    {selectedCampaign.failed_count > 0 && (
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => handleRetryFailed(selectedCampaign.id)}
                        sx={{ alignSelf: 'flex-start' }}
                      >
                        Retry Failed
                      </Button>
                    )}

                    <Grid container spacing={2}>
                      {statsSummary.map((item) => (
                        <Grid item xs={6} md={3} key={item.label}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              {item.label}
                            </Typography>
                            <Typography variant="h5" fontWeight={700}>
                              {item.value}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>

                    {recipientsLoading && <LinearProgress />}

                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>الرقم</TableCell>
                            <TableCell>الحالة</TableCell>
                            <TableCell>المحتوى</TableCell>
                            <TableCell>آخر تحديث</TableCell>
                            <TableCell>الخطأ</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recipients.map((recipient) => (
                            <TableRow key={recipient.id}>
                              <TableCell>{recipient.phone_e164}</TableCell>
                              <TableCell>
                                <Chip
                                  label={recipient.send_status}
                                  size="small"
                                  color={statusColorMap[recipient.send_status] || 'default'}
                                />
                              </TableCell>
                              <TableCell sx={{ maxWidth: 260 }}>
                                <Typography variant="body2" noWrap title={recipient.rendered_content}>
                                  {recipient.rendered_content || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>{formatDateTime(recipient.last_status_at)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" color="error.main">
                                  {recipient.error_message || '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}

                          {!recipientsLoading && recipients.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={5}>لا توجد بيانات للمستلمين.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      <TablePagination
                        component="div"
                        count={recipientsTotal}
                        page={recipientsPage}
                        onPageChange={(_event, nextPage) => loadRecipients(selectedCampaign.id, nextPage, recipientsPageSize)}
                        rowsPerPage={recipientsPageSize}
                        onRowsPerPageChange={(event) => {
                          const nextSize = Number(event.target.value) || 100;
                          loadRecipients(selectedCampaign.id, 0, nextSize);
                        }}
                        rowsPerPageOptions={[25, 50, 100, 250]}
                        labelRowsPerPage="عدد الصفوف"
                      />
                    </TableContainer>
                  </Stack>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
